import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { Grid } from "semantic-ui-react";
import { ProductId } from "../product";
import { Spinner } from "../../utils/Spinner";
import { useGetCustomerQuery } from "../../api/customerApi";
import { useGetProductQuery } from "../../api/productDetailSlice";
import {
  useGetPrompts,
  useGetPromptGroups,
  useGetTextBlocks,
  useGetProductSpecificOverwrites,
} from "./customHooks";
import { SelectedPrompt } from "./Selected/SelectedPrompt";
import { GeneratedResults } from "./GeneratedResults/GeneratedResults";
import {
  CopyAssistantContext,
  CopyAssistantDispatchContext,
  copyAssistantReducer,
  initialCopyAssistantState,
} from "./CopyAssistantContext/CopyAssistantContext";
import { CopyAssistantActionType } from "./CopyAssistantContext/types";
import { SelectedPromptGroup } from "./Selected/SelectedPromptGroup";
import { useGetFieldsets } from "../manage/fieldsets/customhooks";
import { ToolbarTop } from "./Toolbar/ToolbarTop";
import {
  useGetDocumentSections,
  useGetDocumentStructures,
} from "../../planner/document-structure/manage/customhooks";
import { Sentence, SentenceSectionInfo, SentenceTemplateInfo } from "./types";
import { generateId } from "../../utils/uuidUtils";
import {
  buildSentence,
  findConnectedChannelsForSection,
  findLanguage,
  findLanguagesToTranslateForSection,
  getCustomerEnglishLanguage,
  getJobAutoTranslateFlags,
  getSectionContext,
  removeJobAutoTranslateFlags,
  sortTextBlocks,
  updateSentenceValueFactory,
} from "./utils";
import { useUrlState } from "../../utils/react-custom-hooks/urlStateHandler";
import {
  AddSentenceToProductTextBlockResponse,
  GenerateOneResponse,
  GptGenerationTaskQueue,
  createProductSpecificOverwrite,
} from "../../api/gptApi";
import { useSelector } from "react-redux";
import { RootState } from "../../utils/store";
import { setShouldRefreshSentences } from "../../api/copyAssistantSlice";
import { store } from "../../utils/store";
import { getCustomerJobs, JOBS_BASE_URL } from "../../api/jobsApi";
import { Job } from "../../customers/jobs/types";
import { LangStringObject } from "../../vocabulary/LangString";
import {
  NotificationAppearance,
  setDjangoToastOpen,
} from "../../api/djangoToastSlice";
import { reloadPreview } from "../../legacy/t.product-detail";

interface Props {
  productId: ProductId;
}

export const ProductDetailCopyAssistant: React.FC<Props> = ({ productId }) => {
  const token = useSelector((state: RootState) => state.auth.token);
  const mounted = useRef(false);
  const resumedJobsRef = useRef<Map<number, string>>(new Map());
  const isResumingRef = useRef(false);
  const hasResumedRef = useRef(false);
  const [state, dispatch] = useReducer(copyAssistantReducer, {
    ...initialCopyAssistantState,
    productId,
  });
  const {
    data: customer,
    isLoading: isCustomerLoading,
  } = useGetCustomerQuery();
  const { data: product, isLoading: isProductLoading } = useGetProductQuery(
    productId
  );

  const { setParam, removeParam, urlState } = useUrlState<{
    prompt_id: number;
    group_id: number;
  }>();

  const {
    data: { fieldsets, metadata },
    isFetching: isFetchingFieldsets,
  } = useGetFieldsets({ productId });

  const {
    data: sections,
    isFetching: isFetchingSections,
  } = useGetDocumentSections();

  const {
    data: structures,
    isFetching: isFetchingStructures,
  } = useGetDocumentStructures();

  const {
    data: textBlocks,
    isFetching: isFetchingTextBlocks,
  } = useGetTextBlocks(productId);

  const {
    data: productSpecificOverwrites,
    isFetching: isFetchingProductSpecificOverwrite,
  } = useGetProductSpecificOverwrites(productId);

  const {
    data: {
      prompts,
      selected_prompt,
      selected_prompt_group,
      use_channel_as_default_fallback,
    },
    isFetching: isFetchingPrompts,
    reFetch: reFetchPrompts,
  } = useGetPrompts({ productId: productId });

  const {
    data: groups,
    isFetching: isFetchingGroups,
    reFetch: reFetchGroups,
  } = useGetPromptGroups();

  const gptGenerationTaskQueue = useMemo(() => {
    if (token) return new GptGenerationTaskQueue(token);
  }, [token]);

  const resumeInFlightGenerations = useCallback(async (): Promise<void> => {
    // Prevent concurrent calls
    if (isResumingRef.current) {
      return;
    }

    if (
      !gptGenerationTaskQueue ||
      !token ||
      !customer ||
      !structures ||
      !prompts?.length
    ) {
      return;
    }

    isResumingRef.current = true;
    try {
      const { jobs } = await getCustomerJobs(
        token,
        1,
        undefined,
        "PENDING",
        undefined,
        undefined,
        true,
        50
      );

      jobs
        .filter(
          (job) =>
            job.action_name === "GptGenerateOne" &&
            job.arguments?.product_id === productId
        )
        .forEach((job: Job) => {
          if (resumedJobsRef.current.has(job.id)) {
            return;
          }

          const promptId: number | undefined = job.arguments?.prompt_id;
          const prompt = prompts.find(({ id }) => id === promptId) ?? null;

          const languageInfo =
            findLanguage(prompt, customer) ??
            getCustomerEnglishLanguage(customer);
          const languageCode = languageInfo?.code;
          if (!languageCode) {
            return;
          }

          let placeholderSentence =
            state.sentences.list.find(
              (sentence: Sentence) => sentence.generationInfo?.jobId === job.id
            ) ?? null;

          if (!placeholderSentence) {
            placeholderSentence =
              state.sentences.list.find(
                (sentence: Sentence) =>
                  sentence.generationInfo?.promptId === promptId &&
                  (sentence.isLoading || sentence.isStreaming)
              ) ?? null;
          }

          if (placeholderSentence) {
            dispatch({
              type: CopyAssistantActionType.UPDATE_SENTENCE,
              payload: {
                id: placeholderSentence.id,
                toUpdate: {
                  generationInfo: {
                    ...(placeholderSentence.generationInfo || {}),
                    jobId: job.id,
                    promptId,
                    instruction:
                      job.arguments?.instruction ??
                      placeholderSentence.generationInfo?.instruction,
                    inputText:
                      job.arguments?.input_data ??
                      placeholderSentence.generationInfo?.inputText,
                    // Preserve auto-translate flags if they exist
                    autoTranslateAfterGeneration:
                      placeholderSentence.generationInfo
                        ?.autoTranslateAfterGeneration,
                    mtConfigGroupId:
                      placeholderSentence.generationInfo?.mtConfigGroupId,
                  },
                  isLoading: true,
                  isStreaming: Boolean(job.arguments?.stream),
                  loadingReason: "Generating...",
                },
              },
            });
          } else {
            placeholderSentence = buildSentence(
              [],
              {
                inputText: job.arguments?.input_data ?? "",
                instruction: job.arguments?.instruction ?? "",
                promptId,
                jobId: job.id,
              },
              customer,
              structures ?? [],
              languageCode
            );

            placeholderSentence.value =
              placeholderSentence.value ||
              ({ [languageCode]: "" } as LangStringObject);
            placeholderSentence.isLoading = true;
            placeholderSentence.isStreaming = Boolean(job.arguments?.stream);
            placeholderSentence.loadingReason = "Generating...";

            // Retrieve auto-translate flags from localStorage if they exist
            // This restores "Generate & Translate" behavior after page refresh
            const autoTranslateFlags = getJobAutoTranslateFlags(job.id);

            placeholderSentence.generationInfo = {
              ...(placeholderSentence.generationInfo || {}),
              inputText: job.arguments?.input_data ?? "",
              instruction: job.arguments?.instruction ?? "",
              promptId,
              jobId: job.id,
              autoTranslateAfterGeneration:
                autoTranslateFlags?.autoTranslateAfterGeneration ?? false,
              mtConfigGroupId: autoTranslateFlags?.mtConfigGroupId,
            };

            dispatch({
              type: CopyAssistantActionType.ADD_SENTENCE,
              payload: placeholderSentence,
            });

            if (promptId) {
              dispatch({
                type: CopyAssistantActionType.SET_SENTENCE_LOADING,
                payload: { loading: true, promptIds: [promptId] },
              });
            }
          }

          resumedJobsRef.current.set(job.id, placeholderSentence.id);

          const updateSentenceCallback = (value: string): void => {
            if (!mounted.current) return;
            dispatch({
              type: CopyAssistantActionType.UPDATE_SENTENCE,
              payload: {
                id: placeholderSentence.id,
                toUpdate: {
                  value: { [languageCode]: value },
                  isStreaming: true,
                },
              },
            });
          };

          const {
            awaitableTimeouts,
            updateResult,
          } = updateSentenceValueFactory(languageCode, updateSentenceCallback);

          gptGenerationTaskQueue
            .resumeJob(
              job.id,
              `${JOBS_BASE_URL}/${job.id}/stream`,
              `${JOBS_BASE_URL}/${job.id}`,
              updateResult
            )
            .then(async (result: GenerateOneResponse) => {
              await Promise.all(awaitableTimeouts);
              if (!mounted.current) return;

              const sentenceId =
                resumedJobsRef.current.get(job.id) ?? placeholderSentence.id;

              if (!result?.generated_texts?.length) {
                dispatch({
                  type: CopyAssistantActionType.REMOVE_SENTENCE,
                  payload: sentenceId,
                });
              } else {
                const langStringValues = result.generated_texts;
                const primaryLangString = langStringValues[0] || {};
                let sectionContext:
                  | {
                      templateInfo?: SentenceTemplateInfo;
                      sectionInfo?: SentenceSectionInfo;
                    }
                  | undefined;

                if (
                  result.text_block_id &&
                  result.document_section_id &&
                  result.document_section_name
                ) {
                  const data: AddSentenceToProductTextBlockResponse = {
                    text_block_id: result.text_block_id,
                    document_section_id: result.document_section_id,
                    document_section_name: result.document_section_name,
                    lang_string_values: primaryLangString,
                  };
                  sectionContext = getSectionContext(
                    data,
                    customer,
                    structures ?? []
                  );
                }

                dispatch({
                  type: CopyAssistantActionType.UPDATE_SENTENCE,
                  payload: {
                    id: sentenceId,
                    toUpdate: {
                      ...(sectionContext ?? {}),
                      isLoading: false,
                      isStreaming: false,
                      value: primaryLangString,
                    },
                  },
                });

                if (sectionContext?.sectionInfo?.documentSectionName) {
                  dispatch({
                    type: CopyAssistantActionType.ADD_ITEM_ORDER_SENTENCE_LIST,
                    payload: sectionContext.sectionInfo.documentSectionName,
                  });
                  try {
                    const channelIds =
                      sectionContext.sectionInfo.connectedChannels?.map(
                        ({ id }) => id
                      ) || [];
                    if (channelIds.length) {
                      reloadPreview(channelIds, languageCode);
                    }
                  } catch {
                    // ignore preview reload errors
                  }
                }

                dispatch({
                  type: CopyAssistantActionType.SET_SECTION_ACTIVE,
                  payload: { id: sentenceId },
                });

                // Check if this sentence should be auto-translated after generation
                // This restores the "Generate & Translate" behavior after page refresh
                if (
                  placeholderSentence.generationInfo
                    ?.autoTranslateAfterGeneration
                ) {
                  dispatch({
                    type:
                      CopyAssistantActionType.ADD_SENTENCE_AUTO_TRANSLATE_LIST,
                    payload: {
                      id: sentenceId,
                      mtConfigGroupId:
                        placeholderSentence.generationInfo.mtConfigGroupId,
                    },
                  });
                  // Clean up auto-translate flags from localStorage after job completes
                  removeJobAutoTranslateFlags(job.id);
                }

                store.dispatch(
                  setDjangoToastOpen({
                    appearance: NotificationAppearance.INFO,
                    content: `Generation completed in ${
                      result.seconds_spent_generating
                    } seconds${prompt ? ` for ${prompt.name}` : ""}`,
                  })
                );
              }

              dispatch({
                type: CopyAssistantActionType.SET_SENTENCE_LOADING,
                payload: { loading: false, promptIds: [] },
              });
              dispatch({
                type: CopyAssistantActionType.FETCH_NEW_PROMPT_INPUT,
                payload: true,
              });
              resumedJobsRef.current.delete(job.id);
            })
            .catch((error) => {
              if (!mounted.current) return;
              const sentenceId =
                resumedJobsRef.current.get(job.id) ?? placeholderSentence.id;
              dispatch({
                type: CopyAssistantActionType.REMOVE_SENTENCE,
                payload: sentenceId,
              });
              dispatch({
                type: CopyAssistantActionType.SET_SENTENCE_LOADING,
                payload: { loading: false, promptIds: [] },
              });
              resumedJobsRef.current.delete(job.id);
              const errorMessage = error instanceof Error ? error.message : "Generation job failed";
              store.dispatch(
                setDjangoToastOpen({
                  appearance: NotificationAppearance.ERROR,
                  content: `<strong>Error:</strong> ${errorMessage}`,
                })
              );
            });
        });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to resume GPT generation jobs", error);
    } finally {
      isResumingRef.current = false;
    }
  }, [
    customer,
    dispatch,
    gptGenerationTaskQueue,
    productId,
    prompts,
    state.sentences.list,
    structures,
    token,
  ]);

  const promptAndGroupSelectionFromProductSpecificOverwrites = useMemo(() => {
    if (productSpecificOverwrites.length)
      return {
        promptId: productSpecificOverwrites[0].prompt_id,
        groupId: productSpecificOverwrites[0].group_id,
      };

    return {
      promptId: undefined,
      groupId: undefined,
    };
  }, [productSpecificOverwrites]);
  const [hasCreatedKnownSentences, setHasCreatedKnownSentences] = useState(
    false
  );

  const createKnownSentences = (): void => {
    const sentences: Sentence[] = [];
    const mainTextBlocks = sortTextBlocks(
      textBlocks.filter((block) => !block.translated_from),
      groups,
      state.groups.selected
    );
    const allTranslatedTextBlocks = textBlocks.filter(
      (block) => block.translated_from
    );
    // We need to reverse the list to set the correct ordering in the context for the sections
    // the last added section to the ordering is the one that will be displayed first in the list
    mainTextBlocks.reverse().forEach((textBlock) => {
      const langStringValues = {
        [textBlock.language]: textBlock.text,
      };
      const currentTranslatedTextBlocks = allTranslatedTextBlocks.filter(
        (translatedTextBlock) =>
          translatedTextBlock.translated_from === textBlock.id
      );
      currentTranslatedTextBlocks.forEach((translatedTextBlock) => {
        langStringValues[translatedTextBlock.language] =
          translatedTextBlock.text;
      });
      const prompt = prompts.find(
        (prompt) => prompt.id === textBlock.prompt_id
      );
      const connectedChannels = findConnectedChannelsForSection(
        textBlock.section_id,
        customer,
        structures
      );
      const languagesToTranslate = isProductLoading
        ? []
        : findLanguagesToTranslateForSection(
            customer,
            product,
            connectedChannels.map((channel) => channel.id)
          );
      const sentence: Sentence = {
        id: generateId(),
        value: langStringValues,
        prompt_id: prompt?.id,
        language: prompt?.language || textBlock?.language,
        generationInfo: {
          promptId: prompt?.id,
          instruction: prompt?.instruction,
        },
        sectionInfo: {
          documentSectionId: textBlock.section_id,
          documentSectionName: sections.find(
            (section) => section.id === textBlock.section_id
          )?.name,
          textBlockId: textBlock.id,
          langStringValues: langStringValues,
          connectedChannels,
          active: textBlock.active,
        },
        languagesToTranslate,
      };
      if (sentence.sectionInfo.documentSectionName) {
        dispatch({
          type: CopyAssistantActionType.ADD_ITEM_ORDER_SENTENCE_LIST,
          payload: sentence.sectionInfo.documentSectionName,
        });
      }
      sentences.push(sentence);
    });
    dispatch({
      type: CopyAssistantActionType.ADD_SENTENCES,
      payload: sentences,
    });
    setHasCreatedKnownSentences(true);
  };

  // Initial creation of sentences
  useEffect(() => {
    if (
      !isFetchingSections &&
      !isFetchingTextBlocks &&
      !isFetchingPrompts &&
      !isFetchingStructures &&
      !isCustomerLoading &&
      !isProductLoading &&
      !hasCreatedKnownSentences
    ) {
      createKnownSentences();
    }
  }, [
    isFetchingSections,
    isFetchingTextBlocks,
    isFetchingPrompts,
    isFetchingStructures,
    isCustomerLoading,
    isProductLoading,
  ]);

  const shouldRefreshSentences = useSelector(
    (state: RootState) => state.copyAssistant.shouldRefreshSentences
  );
  // Refresh sentences on trigger
  useEffect(() => {
    if (
      shouldRefreshSentences &&
      !isFetchingSections &&
      !isFetchingTextBlocks &&
      !isFetchingPrompts &&
      !isFetchingStructures &&
      !isCustomerLoading &&
      !isProductLoading
    ) {
      setHasCreatedKnownSentences(false);
      createKnownSentences();
      store.dispatch(setShouldRefreshSentences(false));
    }
  }, [
    shouldRefreshSentences,
    isFetchingSections,
    isFetchingTextBlocks,
    isFetchingPrompts,
    isFetchingStructures,
    isCustomerLoading,
    isProductLoading,
  ]);

  useEffect(() => {
    mounted.current = true;
    return (): void => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      isCustomerLoading ||
      isProductLoading ||
      isFetchingSections ||
      isFetchingTextBlocks ||
      isFetchingPrompts ||
      isFetchingStructures ||
      !prompts?.length ||
      !customer ||
      !structures ||
      isResumingRef.current ||
      hasResumedRef.current
    ) {
      return;
    }
    hasResumedRef.current = true;
    resumeInFlightGenerations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isCustomerLoading,
    isProductLoading,
    isFetchingSections,
    isFetchingTextBlocks,
    isFetchingPrompts,
    isFetchingStructures,
    prompts?.length,
    customer?.slug,
    structures?.length,
    // Note: resumeInFlightGenerations is intentionally excluded to prevent infinite loops
    // It's stable due to useCallback with proper dependencies
  ]);

  useEffect(() => {
    dispatch({
      type: CopyAssistantActionType.SET_PRODUCT_SPECIFIC_OVERWRITES_LOADING,
      payload: isFetchingProductSpecificOverwrite,
    });
    if (!isFetchingProductSpecificOverwrite && mounted.current) {
      dispatch({
        type: CopyAssistantActionType.SET_PRODUCT_SPECIFIC_OVERWRITES,
        payload: productSpecificOverwrites,
      });
    }
  }, [isFetchingProductSpecificOverwrite]);

  useEffect(() => {
    if (!isFetchingFieldsets && mounted.current) {
      dispatch({
        type: CopyAssistantActionType.SET_FIELDSETS,
        payload: {
          fieldsets,
          metadata,
        },
      });
    }
  }, [isFetchingFieldsets]);

  const saveLastUsedPromptOrGroup = async (
    promptId: number,
    groupId?: number
  ): Promise<void> => {
    await createProductSpecificOverwrite(token, {
      product_id: productId,
      prompt_id: promptId,
      group_id: groupId || null,
    });
  };

  useEffect(() => {
    if (
      !state.prompts.selected &&
      !state.groups.selected &&
      isFetchingProductSpecificOverwrite
    )
      return;

    if (state.prompts.selected && !state.groups.selected) {
      setParam("prompt_id", state.prompts.selected.id);
      removeParam("group_id");

      saveLastUsedPromptOrGroup(state.prompts.selected.id);
    } else if (state.groups.selected && !state.prompts.selected) {
      setParam("group_id", state.groups.selected.id);
      removeParam("prompt_id");
      saveLastUsedPromptOrGroup(
        state.groups.selected.prompt_ids[0], // a prompt needs to be present when saving.
        state.groups.selected.id // Passing in the group id here makes next time user visits this product this group will be selected
      );
    }
  }, [
    state.prompts.selected,
    state.groups.selected,
    isFetchingProductSpecificOverwrite,
  ]);

  useEffect(() => {
    if (
      !isFetchingPrompts &&
      !isFetchingGroups &&
      !isFetchingProductSpecificOverwrite &&
      mounted.current
    ) {
      let selectedPromptId =
        promptAndGroupSelectionFromProductSpecificOverwrites.promptId;
      let selectedGroupId =
        promptAndGroupSelectionFromProductSpecificOverwrites.groupId;
      if (!selectedPromptId && !selectedGroupId) {
        selectedPromptId = urlState.prompt_id;
        selectedGroupId = urlState.group_id;
      }
      let selectedPrompt = selected_prompt;
      let selectedGroup = selected_prompt_group;
      if (selectedGroupId) {
        const foundGroupFromUrl = groups.find(
          (group) => group.id === selectedGroupId
        );
        if (!foundGroupFromUrl) {
          removeParam("group_id");
        } else {
          selectedGroup = foundGroupFromUrl;
          selectedPrompt = null;
        }
      } else if (selectedPromptId) {
        const foundPromptFromUrl = prompts.find(
          (prompt) => prompt.id === selectedPromptId
        );
        if (!foundPromptFromUrl) {
          removeParam("prompt_id");
        } else {
          selectedPrompt = foundPromptFromUrl;
          selectedGroup = null;
        }
      }

      dispatch({
        type: CopyAssistantActionType.SET_PROMPT_INIT_STATE,
        payload: {
          isLoading: false,
          list: prompts,
          selected: selectedPrompt,
          selectedGroup: selectedGroup,
        },
      });
    }
  }, [isFetchingPrompts, isFetchingGroups, isFetchingProductSpecificOverwrite]);

  useEffect(() => {
    if (!isFetchingGroups && mounted.current) {
      dispatch({
        type: CopyAssistantActionType.SET_GROUP_INIT_STATE,
        payload: {
          isLoading: false,
          list: groups,
        },
      });
    }
  }, [isFetchingGroups]);

  useEffect(() => {
    if (state.prompts.selected || state.groups.selected)
      dispatch({
        type: CopyAssistantActionType.FETCH_NEW_PROMPT_INPUT,
        payload: true,
      });
  }, [state.prompts.selected, state.groups.selected]);

  const onRefreshPromptList = async (): Promise<void> => {
    dispatch({
      type: CopyAssistantActionType.SET_PROMPT_LOADING,
      payload: true,
    });
    await reFetchPrompts();
    await reFetchGroups();
  };
  const onRefreshGroupList = async (): Promise<void> => {
    dispatch({
      type: CopyAssistantActionType.SET_GROUP_LOADING,
      payload: true,
    });

    await reFetchGroups();
    await reFetchPrompts();
  };
  if (isCustomerLoading || isProductLoading || !token) {
    return <Spinner />;
  }

  return (
    <CopyAssistantContext.Provider value={state}>
      <CopyAssistantDispatchContext.Provider value={dispatch}>
        <ToolbarTop
          refreshPromptList={onRefreshPromptList}
          refetchGroups={onRefreshGroupList}
          productId={productId}
        />
        <Grid divided="vertically">
          <Grid.Row columns={2} divided>
            <Grid.Column>
              <SelectedPrompt
                productId={productId}
                refreshPrompts={onRefreshPromptList}
                useChannelAsDefaultFallback={use_channel_as_default_fallback}
                gptGenerationTaskQueue={gptGenerationTaskQueue}
              />
              <SelectedPromptGroup
                reFetchPrompts={onRefreshPromptList}
                useChannelAsDefaultFallback={use_channel_as_default_fallback}
                gptGenerationTaskQueue={gptGenerationTaskQueue}
              />
            </Grid.Column>
            <Grid.Column>
              <GeneratedResults
                reFetchPrompts={onRefreshPromptList}
                gptGenerationTaskQueue={gptGenerationTaskQueue}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </CopyAssistantDispatchContext.Provider>
    </CopyAssistantContext.Provider>
  );
};
